import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { downloadCsv } from '@/lib/csv'

// A16.42 — Eksport markazi. Modul-lokal (adapters.ts / i18n / screenStatus HOT — tegilmaydi).
// Har qator — REAL analitika endpointi; «Eksport» bosilganda javob olinadi va generic
// JSON→CSV bilan yuklab olinadi. Backend REAL: barcha `/admin/analytics/*` agregatlari.

interface ExportItem {
  id: string
  name: string
  path: string
  category: string
}

// Faqat REAL, mavjud (cmd/server/main.go da ro'yxatdan o'tgan) agregat endpointlar.
const EXPORTS: ExportItem[] = [
  { id: 'A16.34', name: 'Bot ekotizimi statistikasi', path: '/admin/analytics/bots', category: 'Bot' },
  { id: 'A16.36', name: 'Top botlar', path: '/admin/analytics/top-bots', category: 'Bot' },
  { id: 'A16.12', name: 'Sessiya davomiyligi', path: '/admin/analytics/sessions', category: 'Foydalanuvchi' },
  { id: 'A16.03', name: 'Retention (D1/D7/D30)', path: '/admin/analytics/retention', category: 'Foydalanuvchi' },
  { id: 'A16.09', name: "Til bo‘yicha", path: '/admin/analytics/languages', category: 'Foydalanuvchi' },
  { id: 'A16.07', name: "Versiya bo‘yicha", path: '/admin/analytics/versions', category: 'Foydalanuvchi' },
  { id: 'A16.05', name: 'Faollik heatmap', path: '/admin/analytics/activity-matrix', category: 'Faollik' },
  { id: 'A16.31', name: 'Ball iqtisodiyoti', path: '/admin/analytics/points', category: 'Iqtisodiyot' },
  { id: 'A16.27', name: "Kanal o‘sishi", path: '/admin/analytics/channel-growth', category: 'Kanal' },
  { id: 'A16.33', name: 'Kanal boostlari', path: '/admin/analytics/channel-boosts', category: 'Kanal' },
  { id: 'A16.37', name: 'Moderatsiya KPI', path: '/admin/analytics/moderation', category: 'Moderatsiya' },
  { id: 'A16.38', name: 'Support KPI', path: '/admin/analytics/support', category: 'Support' },
  { id: 'A16.32', name: 'Reklama daromadi', path: '/admin/analytics/ad-revenue', category: 'Daromad' },
  { id: 'A16.23', name: "Daromad dashboard'i", path: '/admin/analytics/revenue', category: 'Daromad' },
  { id: 'A16.24', name: 'ARPU / ARPPU', path: '/admin/analytics/arpu', category: 'Daromad' },
  { id: 'A16.29', name: 'Taklif havolalari', path: '/admin/analytics/invites', category: 'O‘sish' },
]

const STR = {
  title: 'Eksport markazi',
  note: "Analitika agregatlarini CSV formatida yuklab olish. Har qator jonli `/admin/analytics/*` endpointidan real ma’lumot oladi.",
  colReport: 'Hisobot',
  colCategory: 'Kategoriya',
  colId: 'ID',
  export: 'Eksport (CSV)',
  failed: 'Eksport amalga oshmadi',
} as const

type Json = unknown

function isPlainObject(v: Json): v is Record<string, Json> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
function isScalar(v: Json): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
}
/** CSV-hujayra: boolean → matn, aks holda skalyar/JSON. */
function cell(v: Json): string | number {
  if (typeof v === 'number' || typeof v === 'string') return v
  if (typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

/** Generic JSON→CSV: skalyar maydonlar key/value; obyekt-massivlar sarlavha+qatorlar bo'lib. */
function jsonToTable(data: Json): (string | number)[][] {
  const rows: (string | number)[][] = []
  const arrayToRows = (arr: Json[]): void => {
    const first = arr[0]
    if (isPlainObject(first)) {
      const keys = Object.keys(first)
      rows.push(keys)
      for (const el of arr) {
        if (isPlainObject(el)) rows.push(keys.map((k) => cell(el[k])))
      }
    } else {
      rows.push(['value'])
      for (const el of arr) rows.push([cell(el)])
    }
  }

  if (Array.isArray(data)) {
    arrayToRows(data)
    return rows
  }
  if (isPlainObject(data)) {
    // 1) skalyar maydonlar — key/value jadval.
    const scalars = Object.entries(data).filter(([, v]) => isScalar(v))
    if (scalars.length) {
      rows.push(['metric', 'value'])
      for (const [k, v] of scalars) rows.push([k, cell(v)])
    }
    // 2) massiv maydonlar — har biri alohida seksiya.
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v) && v.length) {
        rows.push([])
        rows.push([k])
        arrayToRows(v)
      }
    }
    return rows
  }
  rows.push(['value'])
  rows.push([cell(data)])
  return rows
}

/** A16.42 — Eksport markazi (eksport qilinadigan analitika hisobotlari ro‘yxati). */
export function ExportCenter() {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async (item: ExportItem) => {
    setBusy(item.id)
    setError(null)
    try {
      const data = await api.get<Json>(item.path)
      downloadCsv(item.name.replace(/[^\w-]+/g, '-').toLowerCase() || 'export', jsonToTable(data))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title={STR.title} screenId="A16.42" description={STR.note} verified />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error && (
          <div className="mb-3 max-w-3xl rounded-lg border border-danger bg-danger-subtle p-2 text-sm text-danger">
            {STR.failed}: <span className="font-mono text-xs">{error}</span>
          </div>
        )}
        <div className="max-w-3xl overflow-hidden rounded-lg border border-border-default bg-surface-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default text-left text-xs text-tertiary">
                <th className="px-3 py-2 font-medium">{STR.colId}</th>
                <th className="px-3 py-2 font-medium">{STR.colReport}</th>
                <th className="px-3 py-2 font-medium">{STR.colCategory}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {EXPORTS.map((item) => (
                <tr key={item.id} className="hover:bg-surface-1">
                  <td className="px-3 py-2 font-mono text-xs text-tertiary">{item.id}</td>
                  <td className="px-3 py-2 text-primary">{item.name}</td>
                  <td className="px-3 py-2 text-secondary">{item.category}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleExport(item)}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary disabled:opacity-50"
                    >
                      {busy === item.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Download size={13} />
                      )}
                      {STR.export}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
